import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, KeyRound, ShieldCheck } from 'lucide-react';
import OTPInput from '../components/OTPInput';
import Turnstile from '../components/Turnstile';
import { API_BASE_URL } from '../lib/api/client';
import '../styles/auth.css';

const BACKEND_URL = API_BASE_URL;
// Mirrors Turnstile.js's own check: when no site key is configured, that
// component immediately reports an empty token and never renders a widget
// at all (the backend fails OPEN in that case — see app/core/turnstile.py).
// Gating the button on turnstileToken must not apply in that scenario, or
// it would permanently disable the button instead of preserving fail-open.
const TURNSTILE_CONFIGURED = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

// Guard against non-JSON responses (e.g. HTML 502/504 from the load balancer)
async function safeJson(res) {
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    throw new Error('Server error — please try again in a moment.');
  }
  return res.json();
}

export default function ForgotPassword() {
  const [step, setStep] = useState(1); // 1: email, 2: OTP, 3: new password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);

  const navigate = useNavigate();

  // Step 1 — Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/auth/forgot-password/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, turnstile_token: turnstileToken }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Failed to send OTP. Please try again.');
      setSuccess('OTP sent! Check your email inbox (and spam folder).');
      setStep(2);
    } catch (err) {
      // That attempt consumed the Turnstile token; re-arm for a fresh one so a
      // retry isn't rejected as timeout-or-duplicate.
      setTurnstileToken('');
      setTurnstileReset((n) => n + 1);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/auth/forgot-password/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'OTP verification failed. Please try again.');
      setSuccess('OTP verified! Now set your new password.');
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 3 — Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter (e.g. A-Z).');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setError('Password must contain at least one lowercase letter (e.g. a-z).');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError('Password must contain at least one number (e.g. 1-9).');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/auth/forgot-password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, new_password: newPassword }),
      });
      const data = await safeJson(res);
      if (!res.ok) {
        // Handle Pydantic 422 — detail can be an array of {msg, loc, ...}
        let errMsg = 'Password reset failed. Please try again.';
        if (Array.isArray(data.detail)) {
          errMsg = data.detail.map(e => e.msg || e.message || '').filter(Boolean).join(' ');
        } else if (typeof data.detail === 'string') {
          errMsg = data.detail;
        }
        throw new Error(errMsg);
      }
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/auth/forgot-password/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data.detail || 'Failed to resend OTP.');
      setSuccess('A new OTP has been sent to your email.');
      setOtp('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const stepLabels = ['Enter Email', 'Verify OTP', 'New Password'];
  const stepIcons = [<Mail size={18} />, <ShieldCheck size={18} />, <Lock size={18} />];

  return (
    <div className="auth-container">
      <Helmet>
        <title>Forgot Password | TeachGenie™.ai</title>
        <meta name="description" content="Reset your TeachGenie™.ai password securely with an OTP sent to your email." />
      </Helmet>

      {/* Animated background blobs */}
      <div className="auth-background">
        <div className="gradient-1"></div>
        <div className="gradient-2"></div>
      </div>

      <div className="auth-wrapper">
        <div className="auth-card">
          {/* Header */}
          <div className="auth-header">
            <div className="auth-logo">
              <picture>
                <source srcSet="/TechGenieMascot.webp" type="image/webp" />
                <img
                  src="/TechGenieMascot.png"
                  alt="TeachGenie™.ai"
                  className="auth-logo-image"
                  style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover' }}
                />
              </picture>
              <h1>Teach<span style={{ color: '#f97316' }}>Genie</span></h1>
            </div>
            <p className="auth-subtitle">Reset Your Password in 3 Simple Steps</p>
          </div>

          {/* Step Progress Indicator */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '0',
            marginBottom: '28px',
          }}>
            {stepLabels.map((label, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isDone = step > stepNum;
              return (
                <React.Fragment key={stepNum}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                    <div style={{
                      width: '38px',
                      height: '38px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isDone
                        ? '#16a34a'
                        : isActive
                          ? '#f59e0b'
                          : '#e5e7eb',
                      color: isDone || isActive ? 'white' : '#9ca3af',
                      fontWeight: '700',
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      boxShadow: isActive ? '0 0 0 4px rgba(245,158,11,0.2)' : 'none',
                    }}>
                      {isDone ? '✓' : stepNum}
                    </div>
                    <span style={{
                      fontSize: '11px',
                      color: isActive ? '#f59e0b' : isDone ? '#16a34a' : '#9ca3af',
                      fontWeight: isActive ? '700' : '500',
                      whiteSpace: 'nowrap',
                    }}>{label}</span>
                  </div>
                  {i < stepLabels.length - 1 && (
                    <div style={{
                      height: '2px',
                      width: '48px',
                      background: step > stepNum ? '#16a34a' : '#e5e7eb',
                      marginBottom: '22px',
                      transition: 'background 0.3s ease',
                    }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="auth-form-container">
            {/* Error / Success messages */}
            {error && (
              <div style={{
                padding: '12px 16px',
                marginBottom: '20px',
                backgroundColor: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#dc2626',
                fontSize: '14px',
              }}>
                ⚠️ {error}
              </div>
            )}
            {success && (
              <div style={{
                padding: '12px 16px',
                marginBottom: '20px',
                backgroundColor: '#dcfce7',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                color: '#16a34a',
                fontSize: '14px',
              }}>
                ✅ {success}
              </div>
            )}

            {/* ── STEP 1: Email ── */}
            {step === 1 && (
              <form onSubmit={handleSendOtp} className="auth-form">
                <div className="form-group">
                  <label>
                    <Mail size={20} />
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '20px' }}>
                  We'll send a 6-digit verification code to this address.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                  <Turnstile onVerify={setTurnstileToken} resetKey={turnstileReset} />
                </div>
                <button
                  type="submit"
                  className="btn btn-accent btn-large"
                  disabled={loading || (TURNSTILE_CONFIGURED && !turnstileToken)}
                  style={{ width: '100%' }}
                >
                  {loading
                    ? <><span className="spinner" /> Sending OTP...</>
                    : (TURNSTILE_CONFIGURED && !turnstileToken) ? 'Verifying you\'re human…' : 'Send OTP'}
                </button>
              </form>
            )}

            {/* ── STEP 2: OTP ── */}
            {step === 2 && (
              <form onSubmit={handleVerifyOtp} className="auth-form">
                <p style={{ color: '#374151', fontSize: '14px', marginBottom: '20px', textAlign: 'center' }}>
                  Enter the 6-digit code sent to <strong>{email}</strong>
                </p>
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  error={null}
                  disabled={loading}
                />
                <button
                  type="submit"
                  className="btn btn-accent btn-large"
                  disabled={loading || otp.length !== 6}
                  style={{ width: '100%', marginTop: '20px' }}
                >
                  {loading ? <><span className="spinner" /> Verifying...</> : 'Verify OTP'}
                </button>
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#f59e0b',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Didn't receive it? Resend OTP
                  </button>
                </div>
              </form>
            )}

            {/* ── STEP 3: New Password ── */}
            {step === 3 && (
              <form onSubmit={handleResetPassword} className="auth-form">
                <div className="form-group">
                  <label>
                    <Lock size={20} />
                    New Password
                  </label>
                  <div className="password-field">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Min 8 chars, 1 uppercase, 1 number"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      autoFocus
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <KeyRound size={20} />
                    Confirm New Password
                  </label>
                  <div className="password-field">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Re-enter your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px' }}>
                      Passwords do not match
                    </p>
                  )}
                </div>

                {/* Password strength hints */}
                <div style={{ marginBottom: '20px', fontSize: '12px', color: '#6b7280' }}>
                  <p style={{ marginBottom: '4px', fontWeight: '600' }}>Password must contain:</p>
                  <ul style={{ paddingLeft: '16px', lineHeight: '1.8' }}>
                    {[
                      { ok: newPassword.length >= 8, text: 'At least 8 characters' },
                      { ok: /[A-Z]/.test(newPassword), text: 'One uppercase letter' },
                      { ok: /[0-9]/.test(newPassword), text: 'One number' },
                    ].map(({ ok, text }) => (
                      <li key={text} style={{ color: ok ? '#16a34a' : '#9ca3af' }}>
                        {ok ? '✓' : '○'} {text}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  type="submit"
                  className="btn btn-accent btn-large"
                  disabled={loading || newPassword !== confirmPassword}
                  style={{ width: '100%' }}
                >
                  {loading ? <><span className="spinner" /> Resetting...</> : 'Reset Password'}
                </button>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="auth-footer" style={{ marginTop: '20px' }}>
            <p>
              Remember your password?{' '}
              <Link to="/login" className="auth-link">Back to Login</Link>
            </p>
          </div>
        </div>

        {/* Right side — tips panel */}
        <div className="auth-benefits">
          <h2>Secure Password Reset</h2>
          <ul>
            <li>
              <span className="benefit-icon">🔐</span>
              <div>
                <h4>OTP Verification</h4>
                <p>A one-time code is sent to your email — no link clicking required</p>
              </div>
            </li>
            <li>
              <span className="benefit-icon">⏱️</span>
              <div>
                <h4>Expires in 10 Minutes</h4>
                <p>Your OTP is time-limited for maximum security</p>
              </div>
            </li>
            <li>
              <span className="benefit-icon">🛡️</span>
              <div>
                <h4>Encrypted at Rest</h4>
                <p>Passwords are hashed and never stored in plain text</p>
              </div>
            </li>
            <li>
              <span className="benefit-icon">✅</span>
              <div>
                <h4>Instant Access</h4>
                <p>Log back in immediately after resetting your password</p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
